import React from 'react';
import { useNavigate } from 'react-router-dom';

const ProductHero = () => {
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error('scrollToSection error:', error);
    }
  };

  return (
    <section className="relative overflow-hidden bg-cream">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[80vh]">
        {/* Left: Light image panel */}
        <div className="relative flex items-center justify-center overflow-hidden h-[180px] sm:h-[260px] lg:h-auto">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/968091b0169f09356f3627dcf71e7f57.png"
              alt="Botanical collection products on linen"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cream/40 via-transparent to-cream/20"></div>
          </div>

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-end pb-8 sm:pb-12 lg:pb-20 px-6 sm:px-8">
          </div>

          <div className="absolute top-6 left-6 sm:top-10 sm:left-10 w-14 h-14 sm:w-20 sm:h-20 rounded-full border border-bronze/10"></div>
          <div className="absolute top-8 left-8 sm:top-12 sm:left-12 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-bronze/20"></div>

          {/* Badge */}
          <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 bg-white/50 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg">
            <div className="flex items-center gap-1.5 mb-0.5">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-award-line text-bronze text-[11px]"></i>
              </div>
              <p className="text-bronze text-[9px] font-sans tracking-[0.25em] uppercase font-semibold">
                Licensed Aromatherapist
              </p>
            </div>
            <p className="text-charcoal text-[12px] sm:text-[13px] font-display italic font-light">
              IFA/NAHA/CFA Certified
            </p>
          </div>
        </div>

        {/* Right: Dark editorial panel */}
        <div className="relative flex items-center justify-start bg-charcoal overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/8875a75abf8dcd0117cf1200b7bc3c37.png"
              alt="Dark botanical arrangement"
              className="w-full h-full object-cover object-center opacity-40"
            />
            <div className="absolute inset-0 bg-[#8A937D]/[0.88]"></div>
          </div>

          <div className="relative z-10 px-6 sm:px-8 lg:px-16 py-10 sm:py-14 lg:py-14 max-w-lg w-full">
            <div className="inline-flex items-center gap-2 mb-5 sm:mb-8">
              <div className="w-7 sm:w-10 h-[1px] bg-softGold/50"></div>
              <span className="text-softGold/70 text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
                Vionne Stability Botanical
              </span>
              <div className="w-7 sm:w-10 h-[1px] bg-softGold/50"></div>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl lg:text-[3.2rem] font-light text-white leading-[1.15] mb-5 sm:mb-8">
              <span className="italic">Botanical</span>
              <br />Neuro&#8209;Modulation
              <br />
              <span className="text-softGold italic">Collection</span>
            </h1>

            <div className="w-12 sm:w-16 h-[1px] bg-gradient-to-r from-softGold/60 to-transparent mb-5 sm:mb-8"></div>

            <p className="text-white/65 text-[12px] sm:text-[13px] font-sans font-light leading-[1.8] sm:leading-[1.9] mb-4 sm:mb-6">
              The Vionne Stability Method begins within the clinic, but meaningful regulation continues through daily rhythms of care.
              Botanical Cocktail Formulation was developed as an extension of this philosophy&mdash;translating clinical observation into personalized botanical support designed for life beyond the treatment room.
            </p>

            <p className="text-softGold/80 text-[11px] sm:text-[12px] font-sans font-light italic leading-relaxed mb-6 sm:mb-10">
              Each preparation becomes a continuation of the Stability process, allowing the body to sustain balance between sessions.
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-6 sm:gap-10 mb-6 sm:mb-10">
              <div>
                <p className="text-white/40 text-[9px] sm:text-[10px] font-sans tracking-wider uppercase mt-0.5 sm:mt-1">Stability Enhancement</p>
              </div>
              <div className="w-[1px] h-8 sm:h-10 bg-white/15"></div>
              <div>
                <p className="text-white/40 text-[9px] sm:text-[10px] font-sans tracking-wider uppercase mt-0.5 sm:mt-1">Botanical Collection</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-2 flex-nowrap mb-8 sm:mb-10">
              <a
                href="/booking"
                onClick={(e) => { e.preventDefault(); navigate('/booking'); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-softGold text-white rounded-full text-[8px] font-sans font-semibold hover:bg-bronze transition-all duration-300 whitespace-nowrap cursor-pointer tracking-[0.2em] uppercase"
              >
                <span>Cocktail Formulation Service</span>
                <i className="ri-arrow-right-line text-[9px]"></i>
              </a>
              <button
                onClick={() => scrollToSection('stability-botanical-collection')}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-transparent border border-white/25 text-white/80 rounded-full text-[8px] font-sans font-medium hover:border-softGold/60 hover:text-softGold transition-all duration-300 whitespace-nowrap cursor-pointer tracking-[0.2em] uppercase"
              >
                <span>Botanical Collection</span>
                <i className="ri-arrow-down-line text-[9px]"></i>
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-[1px] h-6 sm:h-8 bg-gradient-to-b from-softGold/50 to-transparent"></div>
              <span className="text-white/35 text-[8px] sm:text-[9px] font-sans tracking-[0.25em] uppercase">
                Scroll to Explore
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Page Navigation Bar */}
      <div className="relative z-10 border-t border-[#D9CFC4] bg-white">
        <div className="max-w-[1280px] mx-auto px-2 sm:px-8 lg:px-12">
          <nav className="flex items-center gap-0">
            {[
              { label: 'Cocktail Formulation', id: 'cocktail-formulation', icon: 'ri-flask-line' },
              { label: 'Application Formats', id: 'application-formats', icon: 'ri-drop-line' },
              { label: 'Stability Botanical Collection', id: 'stability-botanical-collection', icon: 'ri-leaf-line' },
            ].map((item, i) => (
              <button
                key={i}
                onClick={() => scrollToSection(item.id)}
                className="group flex items-center gap-1 sm:gap-2 px-2 py-3 sm:px-5 sm:py-3.5 text-[8px] sm:text-[10px] font-sans font-semibold tracking-[0.1em] sm:tracking-[0.2em] uppercase text-[#4A4440] hover:text-[#B8A48A] transition-all duration-300 whitespace-nowrap cursor-pointer border-b-[1.5px] border-transparent hover:border-[#B8A48A] relative flex-1 justify-center"
              >
                <div className="hidden sm:flex w-3.5 h-3.5 items-center justify-center">
                  <i className={`${item.icon} text-[11px]`}></i>
                </div>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
    </section>
  );
};

export default ProductHero;
